import React from "react";
import { builder, BuilderComponent } from "@builder.io/react";
import type { GetStaticPropsContext, InferGetStaticPropsType } from "next";

import { dataModel, pagesModel } from "@/builder/model-names";
import { trackExperimentEvent } from "@/builder/utilities";
import { SEO } from "@/components/SEO";
import {
  BUILDER_PAGES_LIMIT,
  REVALIDATION_TIME_SECONDS,
} from "@/lib/constants";
import { capitalizeFirstLetter } from "@/utils/capitalize-first-letter";

import "@/components/Builder/index";

import { localSeoRoutes } from "./consts";

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{ practice: string; stateName: string }>) {
  const page = await builder
    .get(pagesModel.local, {
      url: `${localSeoRoutes.path}/${params?.practice}/${params?.stateName}`,
    })
    .promise();

  const notFound = !page || page?.data?.state?.notFound;

  return {
    props: {
      page,
      isBuilder: true,
    },
    notFound,
    revalidate: REVALIDATION_TIME_SECONDS,
  };
}

interface PathsType {
  params: {
    practice: string;
    stateName: string;
  };
}

export async function getStaticPaths() {
  const localPages = await builder.getAll(dataModel.landingPage, {
    limit: BUILDER_PAGES_LIMIT,
    options: { noTargeting: true },
  });

  const paths: PathsType[] = [];

  localPages.forEach((page) => {
    const practice = page?.data?.practice || "";
    page?.data?.states.forEach((state: { name: string }) => {
      const practiceName = practice.toLowerCase();
      const stateName = state.name.toLowerCase();
      paths.push({ params: { practice: practiceName, stateName } });
    });
  });

  return {
    paths,
    fallback: true,
  };
}

export function LocalSEO({
  page,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const stateName = page?.data?.state?.selectedState || "";
  const practiceName = page?.data?.state?.selectedPractice || "";
  const capitalizedStateName = capitalizeFirstLetter(stateName);
  const capitalizedPracticeName = capitalizeFirstLetter(practiceName);

  // These are the default values for the page title and description. The state and practice names dynamically generated
  const pageTitle = `Local ${capitalizedPracticeName} Attorneys in ${capitalizedStateName} | Marble Law Firm`;
  const pageDescription = `Talk to a ${capitalizedStateName} ${capitalizedPracticeName} lawyer today. Move forward with Marble.`;

  const seo = {
    noIndex: page?.data?.noIndex,
    title: pageTitle,
    description: pageDescription,
    canonicalHref: page?.data?.canonicalHref,
  };

  return (
    <>
      <SEO {...seo} />
      <BuilderComponent
        model={pagesModel.local}
        content={page}
        contentLoaded={(_, content) => {
          trackExperimentEvent(content);
        }}
      />
    </>
  );
}
